<template>
  <div>
    <div class="table-responsive mb-3">
      <div
        class="d-flex justify-content-between align-content-end align-items-center mb-3"
      >
        <h5 class="mb-0">Bank List</h5>
        <b-button size="md" v-on:click="addEditBank()" variant="primary">
          Add New Bank
        </b-button>
      </div>
      <table class="table">
        <thead>
          <tr>
            <th width="80">Sort</th>
            <th>Bank Name</th>
            <th>Image</th>
            <th>Interest Rate</th>
            <th>Interest Rate MRTA</th>
            <th>Maximum Loan Limit (%)</th>
            <th>Maximum Loan Term (Years)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(bank, index) in items()" :key="bank.id || `temp-${index}`">
            <td>
              <b-form-select
                :key="`select-${bank.id || index}-${bank.ordering}`"
                :value="index + 1"
                @change="
                  (value) => onChangeOrder(index, value, index + 1)
                "
              >
                <option v-for="i in data.length" :key="i" :value="i">{{ i }}</option>
              </b-form-select>
            </td>
            <td>
              <div class="text-left">
                <strong>TH:</strong> {{ getBankName(bank, 'th') }}<br />
                <strong>EN:</strong> {{ getBankName(bank, 'en') }}
              </div>
            </td>
            <td>
              <img
                :src="imageSource(bank)"
                alt
                class="bank-image"
                v-if="bank.image_url"
              />
              <span v-else class="text-muted">No image</span>
            </td>
            <td>{{ bank.interest_rate || '-' }}</td>
            <td>{{ bank.interest_rate_mrta || '-' }}</td>
            <td>{{ bank.loan_limit || '-' }}</td>
            <td>{{ bank.loan_period || '-' }}</td>
            <td>
              <b-button-group>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  class="btn-group-space"
                  v-on:click="addEditBank(index, bank)"
                >
                  Edit
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  class="btn-group-space"
                  v-on:click="deleteBank(bank.id, index)"
                >
                  Delete
                </b-button>
              </b-button-group>
            </td>
          </tr>
          <tr v-if="data.length === 0">
            <td colspan="8">
              <div class="text-center">
                <i>Empty data.</i>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <AddLoanBankModal
      :onAddBank="addBank"
      :onUpdateBank="updateBank"
      :data.sync="bankData"
      :onCancel="onCancelModal"
    />
  </div>
</template>

<script>
import AddLoanBankModal from '../modals/AddLoanBankModal'

export default {
  components: {
    AddLoanBankModal,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    onAddBank: Function,
    onUpdateBank: Function,
    onFetchBankList: Function,
    onDeleteBank: Function,
    onSorting: Function,
  },
  data: () => ({
    perPage: 10,
    currentPage: 1,
    bankData: {},
    dataBankTemp: null,
    dataBankTempIndex: null,
  }),
  methods: {
    items() {
      const { currentPage, perPage, data } = this
      const start = (currentPage - 1) * perPage
      const end =
        start + perPage > data.length - 1 ? data.length : start + perPage
      const items = data.slice(start, end)
      return items
    },
    imageSource(bank) {
      const imageUrl = bank.image_url
      if (!imageUrl) return ''
      return `${
        /^http.+/.test(imageUrl) ? '' : process.env.VUE_APP_WEBSITE_ENDPOINT
      }${imageUrl}`
    },
    getBankName(bank, lang) {
      const translation = bank.translations
        ? bank.translations.find((t) => t.lang === lang)
        : null
      return translation ? translation.name : '-'
    },
    onChangeOrder(currentIndex, newValue, oldValue) {
      // Convert 1-based display values to 0-based array indices
      const newIndex = Number(newValue) - 1
      const oldIndex = currentIndex // Use the actual array index, not ordering value

      this.onSorting({
        oldIndex: oldIndex,
        newIndex: newIndex,
      }).then((resp) => {
        if (resp && resp.success) {
          // Force Vue to re-render the table to update dropdown values
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        }
      })
    },
    deleteBank(id, index) {
      this.$bvModal
        .msgBoxConfirm('Are you sure ?', {
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'primary',
          headerClass: 'p-2 border-bottom-0',
          footerClass: 'p-2 border-top-0 justify-center',
          centered: true,
          okOnly: true,
        })
        .then((value) => {
          if (value) {
            this.onDeleteBank(id, index).then((resp) => {
              if (resp && resp.success) {
                // Don't emit update:data - the parent already updated form.banks via onDeleteBank
                // Vue's reactivity will handle the view update automatically
              }
            })
          }
        })
    },
    addBank(data) {
      this.onAddBank(data).then((resp) => {
        if (resp && resp.success) {
          this.$bvModal.hide('add-loan-bank')
          // Don't emit update:data - the parent already updated form.banks via onAddBank
          // Vue's reactivity will handle the view update automatically
          this.$bvToast.toast(resp.message || 'Bank added successfully', {
            title: 'Complete',
            autoHideDelay: 2000,
            appendToast: true,
            variant: 'success',
            toaster: 'b-toaster-bottom-right',
            solid: true,
          })
        } else {
          // Error is already handled by parent component's toast notification
          console.error('Bank operation failed:', resp)
        }
      })
    },
    updateBank(id, data) {
      this.onUpdateBank(id, data).then((resp) => {
        if (resp && resp.success) {
          this.$bvModal.hide('add-loan-bank')
          // Don't emit update:data - the parent already updated form.banks via onUpdateBank
          // Vue's reactivity will handle the view update automatically
          this.$bvToast.toast(resp.message || 'Bank updated successfully', {
            title: 'Complete',
            autoHideDelay: 2000,
            appendToast: true,
            variant: 'success',
            toaster: 'b-toaster-bottom-right',
            solid: true,
          })
        } else {
          // Error is already handled by parent component's toast notification
          console.error('Bank operation failed:', resp)
        }
      })
    },
    addEditBank(index, bank) {
      if (!bank) {
        // Ordering should be 0-based (matches array index)
        const nextOrdering = this.data.length
        this.bankData = {
          translations: [
            { lang: 'th', name: '' },
            { lang: 'en', name: '' },
          ],
          interest_rate: 0,
          interest_rate_mrta: 0,
          loan_limit: 0,
          loan_period: 0,
          ordering: nextOrdering,
          image_url: '',
        }
      } else {
        this.dataBankTemp = JSON.parse(JSON.stringify(bank))
        this.dataBankTempIndex = index
        this.bankData = JSON.parse(JSON.stringify(bank))
      }
      this.$bvModal.show('add-loan-bank')
    },
    onCancelModal() {
      if (this.dataBankTempIndex !== null) {
        this.data[this.dataBankTempIndex] = JSON.parse(
          JSON.stringify(this.dataBankTemp),
        )
      }
    },
  },
  computed: {
    rows() {
      return this.data.length
    },
  },
  created() {
    this.bankData = {
      translations: [
        { lang: 'th', name: '' },
        { lang: 'en', name: '' },
      ],
      interest_rate: 0,
      interest_rate_mrta: 0,
          loan_limit: 0,
          loan_period: 0,
          ordering: 0,
      image_url: '',
    }
  },
}
</script>
<style scoped>
.bank-image {
  max-width: 100px;
  max-height: 60px;
  object-fit: contain;
}
p {
  color: #000000;
}
</style>

